import { FC, useCallback, useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';
import { css, Theme } from '@emotion/react';
import { Users, EnvelopeSimpleOpen, SignOut, Plus, ArrowRight } from '@phosphor-icons/react';
import type { AcceptAccountInviteResponse } from 'api/account/handlers/acceptAccountInviteHandler';
import type { AcceptAccountInviteRequest } from 'common/account/AcceptAccountInviteDocuments';
import { getPlaceholderUrl } from 'common/commonUtil';
import Api from 'client/lib/Api';
import { useError } from 'client/lib/Error';
import { useUser } from 'client/lib/User';
import { Banner } from 'client/components/Banner';
import { Button } from 'client/components/Button';
import { Catalog, CatalogItemProps } from 'client/components/Catalog';
import { DialogContent } from 'client/components/Dialog/DialogContent';
import { Divider } from 'client/components/Divider';
import { useGitHubLogin } from 'client/components/GitHub/useGitHubLogin';
import { Modal } from 'client/components/Layout/Modal';
import { SuccessBanner } from 'client/components/Other/SuccessBanner';
import { Tag } from 'client/components/Tag';
import { LoginWithGitHubButton } from '../components/LoginWithGitHubButton';

const roleButtonStyle = (theme: Theme) => css`
  margin-right: 8px;

  > span:first-of-type > svg {
    transform: translate(-3px, 1px);
    color: ${theme.color.grey[100]};
  }
`;

const successButtonStyle = css`
  margin-top: 16px;
`;

const bannerStyle = css`
  margin-top: 16px;
`;

const acceptInvite = (token: string) =>
  new Promise<AcceptAccountInviteResponse>((resolve, reject) =>
    Api.post<AcceptAccountInviteRequest, AcceptAccountInviteResponse>('/account/invite/accept', { token })
      .then((response) => resolve(response))
      .catch((error) => {
        console.error(error);
        return reject('unexpected error');
      })
  );

export interface AccountInviteProps {
  token: string;
  account: {
    id: number;
    name: string;
    identifier: string;
  };
}

export const AccountInvite: FC<AccountInviteProps> = ({ token, account }) => {
  const { user, isLoggedIn, logout, updateSession } = useUser();
  const { login, isLoading: isGithubLoading, isError: isGithubError } = useGitHubLogin();
  const { setError } = useError();

  const acceptMutation = useMutation({
    mutationFn: acceptInvite,
  });

  const path = window.location.pathname;

  const accountIDs = user?.accounts.map((account) => account.id) || [];
  const isMember = accountIDs.includes(account.id);
  const isSuccess = acceptMutation.data?.status === 'ok';

  const handleAccept = useCallback(async () => {
    if (!token) return false;
    setError(null);

    const { status } = await acceptMutation.mutateAsync(token);

    if (status !== 'ok') {
      setError('Could not accept invite. Please try again…', { status });
    }

    await updateSession();

    return false;
  }, [token, updateSession]);

  const userItem = useMemo<CatalogItemProps>(
    () => ({
      id: 'user',
      title: (
        <>
          <Tag>You</Tag> {[user?.firstName, user?.lastName].filter((item) => !!item).join(' ')}
        </>
      ),
      image: { alt: 'profile picture', src: `https://github.com/${user?.githubUsername}.png?size=32`, shape: 'circle' },
      subtitle: (
        <a href={`https://github.com/${user?.githubUsername}`} target="_blank" style={{ color: 'inherit' }}>
          @{user?.githubUsername}
        </a>
      ),
      action: (
        <>
          <Button icon={<SignOut />} small onClick={() => logout({ skipRedirect: true })} css={roleButtonStyle}>
            Sign Out
          </Button>
        </>
      ),
    }),
    [user, logout]
  );

  const accountItem = useMemo<CatalogItemProps>(
    () => ({
      id: 'account',
      title: account.name,
      image: { alt: 'account profile picture', src: getPlaceholderUrl('account', account.id || 0) },
      action: isMember ? (
        <>
          <Button href={`/${account.identifier}`} icon={<ArrowRight weight="bold" />} small css={roleButtonStyle}>
            View Account
          </Button>
        </>
      ) : undefined,
    }),
    [account, isMember]
  );

  return (
    <Modal>
      <DialogContent
        title="Join an Account"
        description="You have been invited to join an account"
        icon={<Users />}
        iconOrnament={<EnvelopeSimpleOpen />}
        secondaryAction={null}
        primaryAction={isLoggedIn && !isMember && !isSuccess ? 'Join Account' : null}
        primaryActionIcon={<Plus weight="bold" />}
        dismissable={false}
        onPrimary={handleAccept}
      >
        {isSuccess && (
          <SuccessBanner title="You have joined the account" subtitle={`You are now a member of the "${account.name}" account`}>
            <Button primary href={`/${account.identifier}`} icon={<ArrowRight weight="bold" />} small css={successButtonStyle}>
              View Account
            </Button>
          </SuccessBanner>
        )}
        {!isSuccess && isLoggedIn && isMember && (
          <>
            <Catalog items={[accountItem]} />
            <Banner type="success" css={bannerStyle}>
              You are already a member of this account.
            </Banner>
          </>
        )}
        {!isSuccess && isLoggedIn && !isMember && (
          <>
            <Catalog items={[userItem]} />
            <Divider>Will join</Divider>
            <Catalog items={[accountItem]} />
            <Banner css={bannerStyle}>
              Your existing resources will not be shared with this account. You will be able to leave at any time.
            </Banner>
          </>
        )}
        {!isSuccess && !isLoggedIn && (
          <>
            <LoginWithGitHubButton loading={isGithubLoading} onClick={() => login({ token, path })} />
            <Banner css={bannerStyle}>You have to be authenticated in order to join an account.</Banner>
          </>
        )}
      </DialogContent>
    </Modal>
  );
};
